import { useState, useEffect } from 'react'
import { useNavigate, useParams, Link, useLocation, useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import type { Unit, Lesson, Content, Exercise, ExerciseFormData, SkillType, MatchingPairData } from '@/types'
import AudioPlayer from '@/components/common/AudioPlayer'
import {
  ArrowLeft,
  Save,
  PenTool,
  FileText,
  Headphones,
  Pencil,
  BookOpen,
  Edit,
  CheckSquare,
  Type as TypeIcon,
  ArrowRightLeft,
  ListOrdered,
  Languages,
  Volume2
} from 'lucide-react'
import { decodeMatchingPairs, isMatchingEncoded } from '@/lib/exerciseUtils'
import MultipleChoiceEditor from './editors/MultipleChoiceEditor'
import FillBlankEditor from './editors/FillBlankEditor'
import MatchingEditor from './editors/MatchingEditor'
import OrderingEditor from './editors/OrderingEditor'
import TranslationEditor from './editors/TranslationEditor'
import ListeningEditor from './editors/ListeningEditor'

const EXERCISE_TYPES = [
  { value: 'multiple_choice', label: 'Opción Múltiple', icon: CheckSquare, color: 'text-[#0055A4] bg-[#0055A4]/5 border-[#0055A4]/20' },
  { value: 'fill_blank', label: 'Completar Espacios', icon: TypeIcon, color: 'text-amber-600 bg-amber-50 border-amber-200' },
  { value: 'matching', label: 'Emparejar', icon: ArrowRightLeft, color: 'text-purple-600 bg-purple-50 border-purple-200' },
  { value: 'ordering', label: 'Ordenar', icon: ListOrdered, color: 'text-cyan-600 bg-cyan-50 border-cyan-200' },
  { value: 'translation', label: 'Traducción', icon: Languages, color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
  { value: 'listening', label: 'Escucha', icon: Volume2, color: 'text-indigo-600 bg-indigo-50 border-indigo-200' }
]

const SKILLS = [
  { value: 'listening', label: 'Escucha', icon: Headphones, color: 'bg-[#0055A4]/10 text-[#0055A4]', borderColor: 'border-[#0055A4]' },
  { value: 'writing', label: 'Escritura', icon: Pencil, color: 'bg-green-100 text-green-700', borderColor: 'border-green-500' },
  { value: 'reading', label: 'Lectura', icon: BookOpen, color: 'bg-orange-100 text-orange-700', borderColor: 'border-orange-500' }
]

export default function ExerciseForm() {
  const { exerciseId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const filterQuery = searchParams.toString() ? '?' + searchParams.toString() : ''
  const listUrl = `/admin/exercises${filterQuery}`
  const isEditing = Boolean(exerciseId) && exerciseId !== 'new'
  const isViewMode = isEditing && !location.pathname.endsWith('/edit')

  const [units, setUnits] = useState<Unit[]>([])
  const [lessons, setLessons] = useState<Lesson[]>([])
  const [contents, setContents] = useState<Content[]>([])
  const [selectedUnit, setSelectedUnit] = useState<string>('')
  const [selectedLesson, setSelectedLesson] = useState<string>('')

  const [formData, setFormData] = useState<ExerciseFormData>({
    content_id: '',
    skill: 'reading',
    exercise_type: 'multiple_choice',
    question: '',
    options: ['', '', '', ''],
    correct_answer: '',
    explanation: '',
    audio_url: '',
    image_url: '',
    points: 10,
    order_num: 1,
    is_active: true
  })

  // Estado local para tipos especiales
  const [matchingPairs, setMatchingPairs] = useState<MatchingPairData[]>([
    { id: '1', left: '', right: '' },
    { id: '2', left: '', right: '' }
  ])
  const [orderItems, setOrderItems] = useState<string[]>(['', '', ''])

  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    loadUnits()
    if (isEditing) {
      loadExercise()
    }
  }, [exerciseId])

  const loadUnits = async () => {
    try {
      const { data, error } = await supabase
        .from('units')
        .select('*')
        .order('order_num', { ascending: true })

      if (error) throw error
      const mapped = (data || []).map((u: any) => ({
        ...u,
        title: u.title || u.title_es || u.title_fr || u.name_es || u.name_fr || '',
        order_num: u.order_num ?? u.unit_number ?? 0,
      })) as Unit[]
      setUnits(mapped)
    } catch (error) {
      console.error('Error loading units:', error)
    }
  }

  const loadLessons = async (unitId: string) => {
    try {
      const { data, error } = await supabase
        .from('lessons')
        .select('*')
        .eq('unit_id', unitId)
        .order('order_num', { ascending: true })

      if (error) throw error
      const mapped = (data || []).map((l: any) => ({
        ...l,
        title: l.title || l.title_es || l.title_fr || l.name_es || l.name_fr || '',
        order_num: l.order_num ?? l.order_index ?? l.lesson_number ?? 0,
      })) as Lesson[]
      setLessons(mapped)
    } catch (error) {
      console.error('Error loading lessons:', error)
    }
  }

  const loadContents = async (lessonId: string) => {
    try {
      const { data, error } = await supabase
        .from('contents')
        .select('*')
        .eq('lesson_id', lessonId)
        .order('order_num', { ascending: true })

      if (error) throw error
      const contentsData = data as unknown as Content[]
      setContents(contentsData || [])

      if (!isEditing && contentsData && contentsData.length > 0 && !formData.content_id) {
        setFormData(prev => ({ ...prev, content_id: contentsData[0].id }))
        loadNextOrderNum(contentsData[0].id, formData.skill)
      }
    } catch (error) {
      console.error('Error loading contents:', error)
    }
  }

  const loadExercise = async () => {
    setIsLoading(true)
    try {
      const { data, error } = await supabase
        .from('exercises')
        .select(`
          *,
          content:contents(*, lesson:lessons(*, unit:units(*)))
        `)
        .eq('id', exerciseId as string)
        .single()

      if (error) throw error

      type ExerciseWithRelations = Exercise & {
        content: Content & {
          lesson: Lesson & { unit: Unit; unit_id: string }
          lesson_id: string
        }
      }
      const exerciseData = data as unknown as ExerciseWithRelations

      const unitId = exerciseData.content?.lesson?.unit?.id
      const lessonId = exerciseData.content?.lesson_id

      setSelectedUnit(unitId || '')
      await loadLessons(unitId || '')
      setSelectedLesson(lessonId || '')
      await loadContents(lessonId || '')

      const loadedOptions = exerciseData.options || ['', '', '', '']

      setFormData({
        content_id: exerciseData.content_id,
        skill: exerciseData.skill,
        exercise_type: exerciseData.exercise_type,
        question: exerciseData.question,
        options: loadedOptions,
        correct_answer: exerciseData.correct_answer,
        explanation: exerciseData.explanation || '',
        audio_url: exerciseData.audio_url || '',
        image_url: exerciseData.image_url || '',
        points: exerciseData.points,
        order_num: exerciseData.order_num,
        is_active: exerciseData.is_active
      })

      // Decodificar datos especiales segun el tipo
      if (exerciseData.exercise_type === 'matching' && isMatchingEncoded(loadedOptions)) {
        const decoded = decodeMatchingPairs(loadedOptions)
        setMatchingPairs(decoded.map((p, i) => ({ id: String(i + 1), left: p.left, right: p.right })))
      }
      if (exerciseData.exercise_type === 'ordering') {
        setOrderItems(loadedOptions.length > 0 ? loadedOptions : ['', '', ''])
      }
    } catch (error) {
      console.error('Error loading exercise:', error)
      setError('Error al cargar el ejercicio')
    } finally {
      setIsLoading(false)
    }
  }

  const loadNextOrderNum = async (contentId: string, skill: string) => {
    try {
      const { data } = await supabase
        .from('exercises')
        .select('order_num')
        .eq('content_id', contentId)
        .eq('skill', skill)
        .order('order_num', { ascending: false })
        .limit(1)

      const orderData = data as unknown as { order_num: number }[]
      if (orderData && orderData.length > 0) {
        setFormData(prev => ({ ...prev, order_num: orderData[0].order_num + 1 }))
      } else {
        setFormData(prev => ({ ...prev, order_num: 1 }))
      }
    } catch (error) {
      console.error('Error loading order num:', error)
    }
  }

  const handleUnitChange = (unitId: string) => {
    setSelectedUnit(unitId)
    setSelectedLesson('')
    setFormData(prev => ({ ...prev, content_id: '' }))
    setLessons([])
    setContents([])
    if (unitId) {
      loadLessons(unitId)
    }
  }

  const handleLessonChange = (lessonId: string) => {
    setSelectedLesson(lessonId)
    setFormData(prev => ({ ...prev, content_id: '' }))
    setContents([])
    if (lessonId) {
      loadContents(lessonId)
    }
  }

  const handleContentChange = (contentId: string) => {
    setFormData(prev => ({ ...prev, content_id: contentId }))
    if (!isEditing) {
      loadNextOrderNum(contentId, formData.skill)
    }
  }

  const handleSkillChange = (skill: string) => {
    setFormData(prev => ({
      ...prev,
      skill: skill as SkillType
    }))

    if (!isEditing && formData.content_id) {
      loadNextOrderNum(formData.content_id, skill)
    }
  }

  const handleTypeChange = (newType: string) => {
    // Resetear opciones segun el nuevo tipo
    let newOptions: string[] = []
    if (newType === 'multiple_choice' || newType === 'listening') {
      newOptions = ['', '', '', '']
    }

    setFormData(prev => ({
      ...prev,
      exercise_type: newType as any,
      options: newOptions,
      correct_answer: '',
      question: ''
    }))

    // Resetear estados especiales
    setMatchingPairs([
      { id: '1', left: '', right: '' },
      { id: '2', left: '', right: '' }
    ])
    setOrderItems(['', '', ''])
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsSaving(true)

    try {
      const exerciseData = {
        content_id: formData.content_id,
        skill: formData.skill,
        exercise_type: formData.exercise_type,
        question: formData.question,
        options: formData.options.filter(o => o.trim() !== ''),
        correct_answer: formData.correct_answer,
        explanation: formData.explanation || null,
        audio_url: formData.audio_url || null,
        image_url: formData.image_url || null,
        points: formData.points,
        order_num: formData.order_num,
        is_active: formData.is_active
      }

      if (isEditing) {
        const { error } = await (supabase
          .from('exercises') as any)
          .update({
            ...exerciseData,
            updated_at: new Date().toISOString()
          })
          .eq('id', exerciseId as string)

        if (error) throw error
      } else {
        const { error } = await (supabase
          .from('exercises') as any)
          .insert(exerciseData)

        if (error) throw error
      }

      if (isEditing) {
        navigate(`/admin/exercises/${exerciseId}${filterQuery}`)
      } else {
        navigate(listUrl)
      }
    } catch (error: any) {
      console.error('Error saving exercise:', error)
      setError(error.message || 'Error al guardar el ejercicio')
    } finally {
      setIsSaving(false)
    }
  }

  const isValidUrl = (url: string) => {
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0055A4]" />
      </div>
    )
  }

  // Tipos que no necesitan la seccion genérica de pregunta (la manejan internamente)
  const typeHandlesOwnQuestion = ['fill_blank', 'matching', 'ordering', 'translation', 'listening']

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to={listUrl}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isViewMode ? 'Visualizar Ejercicio' : (isEditing ? 'Editar Ejercicio' : 'Nuevo Ejercicio')}
            </h1>
            <p className="text-gray-600">
              {isViewMode ? 'Vista previa del ejercicio' : (isEditing ? 'Modifica los datos del ejercicio' : 'Crea un nuevo ejercicio')}
            </p>
          </div>
        </div>
        {isViewMode && (
          <div className="flex items-center gap-3">
            <Link
              to={`/admin/exercises/${exerciseId}/edit${filterQuery}`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Edit className="w-5 h-5" />
              Editar
            </Link>
            <Link
              to={listUrl}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              OK / Volver
            </Link>
          </div>
        )}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
            {error}
          </div>
        )}

        {/* Location Selection */}
        <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-green-100">
              <FileText className="w-5 h-5 text-green-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Ubicación</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="exercise-unit" className="block text-sm font-medium text-gray-700 mb-1">Unidad</label>
              <select id="exercise-unit" value={selectedUnit}
                onChange={(e) => handleUnitChange(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
                disabled={isViewMode}
              >
                <option value="">Seleccionar...</option>
                {units.map(unit => (
                  <option key={unit.id} value={unit.id}>
                    Unidad {unit.order_num}{unit.title ? `: ${unit.title}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="exercise-lesson"className="block text-sm font-medium text-gray-700 mb-1">Lección</label>
              <select id="exercise-lesson" value={selectedLesson}
                onChange={(e) => handleLessonChange(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
                disabled={!selectedUnit || isViewMode}
              >
                <option value="">Seleccionar...</option>
                {lessons.map(lesson => (
                  <option key={lesson.id} value={lesson.id}>
                    Lección {lesson.order_num}{lesson.title ? `: ${lesson.title}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="exercise-content" className="block text-sm font-medium text-gray-700 mb-1">Contenido</label>
              <select id="exercise-content" value={formData.content_id}
                onChange={(e) => handleContentChange(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
                disabled={!selectedLesson || isViewMode}
              >
                <option value="">Seleccionar...</option>
                {contents.map(content => (
                  <option key={content.id} value={content.id}>{content.title}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Skill Selection */}
        <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Habilidad</h2>
          <div className="grid grid-cols-3 gap-3">
            {SKILLS.map((skill) => {
              const Icon = skill.icon
              return (
                <button
                  key={skill.value}
                  type="button"
                  onClick={() => !isViewMode && handleSkillChange(skill.value)}
                  disabled={isViewMode}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    formData.skill === skill.value
                      ? `${skill.borderColor} bg-[#0055A4]/5`
                      : 'border-gray-200 hover:border-gray-300'
                  } ${isViewMode ? 'cursor-not-allowed opacity-75' : ''}`}
                >
                  <div className={`w-10 h-10 rounded-lg ${skill.color} flex items-center justify-center mx-auto mb-2`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <p className="text-sm font-medium text-gray-900">{skill.label}</p>
                </button>
              )
            })}
          </div>

          {/* Banner contextual por habilidad */}
          {formData.skill === 'listening' && (
            <div className="p-3 bg-[#0055A4]/5 border border-[#0055A4]/20 rounded-lg flex items-start gap-3">
              <Headphones className="w-5 h-5 text-[#0055A4] mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-[#002d5c] text-sm">Ejercicio de Escucha</p>
                <p className="text-xs text-[#0055A4]">
                  Asegúrate de incluir una URL de audio. El estudiante escuchará el audio antes de responder.
                </p>
              </div>
            </div>
          )}
          {formData.skill === 'writing' && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <Pencil className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-green-900 text-sm">Ejercicio de Escritura</p>
                <p className="text-xs text-green-700">
                  Tipos recomendados: Completar Espacios, Traducción. El estudiante practicará producir texto.
                </p>
              </div>
            </div>
          )}
          {formData.skill === 'reading' && (
            <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-start gap-3">
              <BookOpen className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-orange-900 text-sm">Ejercicio de Lectura</p>
                <p className="text-xs text-orange-700">
                  Incluye un texto o pasaje en la pregunta. Puedes agregar una imagen con material adicional.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Exercise Type Selection (visual cards) */}
        <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Tipo de Ejercicio</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {EXERCISE_TYPES.map((type) => {
              const Icon = type.icon
              const isSelected = formData.exercise_type === type.value
              return (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => !isViewMode && handleTypeChange(type.value)}
                  disabled={isViewMode}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${
                    isSelected
                      ? `${type.color} border-current`
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  } ${isViewMode ? 'cursor-not-allowed opacity-75' : ''}`}
                >
                  <Icon className={`w-6 h-6 mb-2 ${isSelected ? '' : 'text-gray-400'}`} />
                  <p className={`text-sm font-medium ${isSelected ? '' : 'text-gray-700'}`}>
                    {type.label}
                  </p>
                </button>
              )
            })}
          </div>
        </div>

        {/* Exercise Details (Dinamic per type) */}
        <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-pink-100">
              <PenTool className="w-5 h-5 text-pink-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Contenido del Ejercicio</h2>
          </div>

          {/* Configuracion basica: puntos y orden */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="exercise-points"className="block text-sm font-medium text-gray-700 mb-1">Puntos</label>
              <input id="exercise-points" type="number"
                min="1"
                max="100"
                value={formData.points}
                onChange={(e) => setFormData(prev => ({ ...prev, points: Number.parseInt(e.target.value) || 10 }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={isViewMode}
              />
            </div>
            <div>
              <label htmlFor="exercise-order" className="block text-sm font-medium text-gray-700 mb-1">Orden</label>
              <input id="exercise-order" type="number"
                min="1"
                value={formData.order_num}
                onChange={(e) => setFormData(prev => ({ ...prev, order_num: Number.parseInt(e.target.value) || 1 }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={isViewMode}
              />
            </div>
          </div>

          {/* Pregunta generica (solo para multiple_choice) */}
          {!typeHandlesOwnQuestion.includes(formData.exercise_type) && (
            <div>
              <label htmlFor="exercise-question" className="block text-sm font-medium text-gray-700 mb-1">
                Pregunta / Instruccion
              </label>
              <textarea id="exercise-question" value={formData.question}
                onChange={(e) => setFormData(prev => ({ ...prev, question: e.target.value }))}
                placeholder="Escribe la pregunta o instrucción del ejercicio..."
                rows={3}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
                disabled={isViewMode}
              />
            </div>
          )}

          {/* Editor dinamico segun tipo */}
          <div className="border-t pt-4">
            {formData.exercise_type === 'multiple_choice' && (
              <MultipleChoiceEditor formData={formData} setFormData={setFormData} isViewMode={isViewMode} />
            )}
            {formData.exercise_type === 'fill_blank' && (
              <FillBlankEditor formData={formData} setFormData={setFormData} isViewMode={isViewMode} />
            )}
            {formData.exercise_type === 'matching' && (
              <MatchingEditor
                formData={formData}
                setFormData={setFormData}
                isViewMode={isViewMode}
                matchingPairs={matchingPairs}
                setMatchingPairs={setMatchingPairs}
                skill={formData.skill}
              />
            )}
            {formData.exercise_type === 'ordering' && (
              <OrderingEditor
                formData={formData}
                setFormData={setFormData}
                isViewMode={isViewMode}
                orderItems={orderItems}
                setOrderItems={setOrderItems}
              />
            )}
            {formData.exercise_type === 'translation' && (
              <TranslationEditor formData={formData} setFormData={setFormData} isViewMode={isViewMode} />
            )}
            {formData.exercise_type === 'listening' && (
              <ListeningEditor formData={formData} setFormData={setFormData} isViewMode={isViewMode} />
            )}
          </div>

          {/* Explicacion (para todos los tipos) */}
          <div>
            <label htmlFor="exercise-explanation" className="block text-sm font-medium text-gray-700 mb-1">
              Explicación (opcional)
            </label>
            <textarea id="exercise-explanation" value={formData.explanation}
              onChange={(e) => setFormData(prev => ({ ...prev, explanation: e.target.value }))}
              placeholder="Explica por que esta es la respuesta correcta..."
              rows={2}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={isViewMode}
            />
          </div>

          {/* Audio e Imagen (no se muestran para listening porque ya tiene su propio campo) */}
          {formData.exercise_type !== 'listening' && (
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="exercise-audio-url" className="block text-sm font-medium text-gray-700 mb-1">
                  URL de Audio (opcional)
                </label>
                <input id="exercise-audio-url" type="url" value={formData.audio_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, audio_url: e.target.value }))}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                  disabled={isViewMode}
                />
                {formData.audio_url && isValidUrl(formData.audio_url) && (
                  <div className="mt-2 p-2 bg-[#0055A4]/5 rounded-lg">
                    <AudioPlayer src={formData.audio_url} className="w-full" />
                  </div>
                )}
              </div>
              <div>
                <label htmlFor="exercise-image-url" className="block text-sm font-medium text-gray-700 mb-1">
                  URL de Imagen (opcional)
                </label>
                <input id="exercise-image-url" type="url" value={formData.image_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                  disabled={isViewMode}
                />
                {formData.image_url && isValidUrl(formData.image_url) && (
                  <div className="mt-2 p-2 bg-gray-50 rounded-lg">
                    <img src={formData.image_url} alt="Preview" className="max-h-32 mx-auto rounded" />
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
              className="w-4 h-4 text-[#0055A4] border-gray-300 rounded focus:ring-[#0055A4] disabled:cursor-not-allowed"
              disabled={isViewMode}
            />
            <label htmlFor="is_active" className="text-sm text-gray-700">
              Ejercicio activo (visible para estudiantes)
            </label>
          </div>
        </div>

        {/* Actions */}
        {!isViewMode && (
          <div className="flex justify-end gap-3">
            <Link
              to={listUrl}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancelar
            </Link>
            <button
              type="submit"
              disabled={isSaving || !formData.content_id}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#0055A4] text-white rounded-lg hover:bg-[#004080] transition-colors disabled:opacity-50"
            >
              <Save className="w-5 h-5" />
              {isSaving ? 'Guardando...' : (isEditing ? 'Guardar Cambios' : 'Crear Ejercicio')}
            </button>
          </div>
        )}
      </form>
    </div>
  )
}
